/** Delade typer och hjälpare för besöksstatistiken (klient + server). */

export const EVENT_KINDS = [
  "pageview",
  "ad_view",
  "search",
  "contact",
  "share",
  "publish_start",
  "publish_submit",
  "publish_done",
  "signup",
  "signup_start",
  "signup_submit",
  "signup_verified",
  "login",
  "upgrade",
  "cookie_choice",
  "consent",
  "heartbeat",
  "click",
  "scroll",
  "exit",
  "filter",
  "category",
  "image_upload",
  "ai_done",
  "ai_locked",
  "ai_unlocked",
  "app_native_ok",
  "app_native_fallback",
] as const;


export type EventKind = (typeof EVENT_KINDS)[number];

/** Vad besökaren gjorde med cookie-rutan – hela förloppet, inte bara valet. */
export const CONSENT_ACTIONS = [
  "shown",
  "settings_opened",
  "policy_opened",
  "accept_all",
  "reject_all",
  "custom",
  "changed",
] as const;

export type ConsentAction = (typeof CONSENT_ACTIONS)[number];

export const CONSENT_LABEL: Record<ConsentAction, string> = {
  shown: "Såg rutan",
  settings_opened: "Öppnade Anpassa",
  policy_opened: "Öppnade policyn",
  accept_all: "Godkände allt",
  reject_all: "Endast nödvändiga",
  custom: "Eget val",
  changed: "Ändrade sig",
};

export type HostKind = "saljavo" | "lovable_preview" | "lovable_published" | "local" | "other";

export const HOST_LABEL: Record<HostKind, string> = {
  saljavo: "sellavo.com",
  lovable_preview: "Lovable förhandsvisning",
  lovable_published: "Lovable publicerad",
  local: "Lokalt",
  other: "Övrigt",
};

/** Grov domängruppering i filterraden: riktig domän, Lovable eller allt. */
export type DomainScope = "own" | "all" | "lovable";

export const DOMAIN_SCOPES: { value: DomainScope; label: string; hint: string }[] = [
  { value: "own", label: "sellavo.com", hint: "Bara besök på den riktiga domänen." },
  { value: "all", label: "Alla domäner", hint: "sellavo.com + Lovable-domänerna + lokalt." },
  {
    value: "lovable",
    label: "Endast Lovable",
    hint: "Förhandsvisning och publicerad Lovable-domän – vår egen testtrafik.",
  },
];

/** Vilka host_kind som ingår i respektive domänval. */
export function hostKindsForScope(scope: DomainScope): HostKind[] | null {
  if (scope === "own") return ["saljavo"];
  if (scope === "lovable") return ["lovable_preview", "lovable_published", "local"];
  return null;
}

/** Grupperar en värddomän till en av våra domänkategorier. */
export function hostKindFromHost(host: string): HostKind {
  const h = (host ?? "").toLowerCase();
  if (h.includes("localhost") || h.startsWith("127.")) return "local";
  if ((h.includes("sellavo") || h.includes("saljavo")) && !h.includes("lovable")) return "saljavo";
  if (h.includes("id-preview") || h.includes("-dev.lovable")) return "lovable_preview";
  if (h.includes("lovable")) return "lovable_published";
  return "other";
}



const OPTOUT_KEY = "sellavo.notrack";

/** True när den här webbläsaren är utesluten ur vår egen statistik. */
export function isTrackingOptedOut(): boolean {
  if (typeof window === "undefined") return false;
  try {
    return window.localStorage.getItem(OPTOUT_KEY) === "1";
  } catch {
    return false;
  }
}

/** Slår på eller av uteslutningen för den här webbläsaren. */
export function setTrackingOptOut(value: boolean) {
  try {
    window.localStorage.setItem(OPTOUT_KEY, value ? "1" : "0");
  } catch {
    /* ignorera blockerad lagring */
  }
}

/** True när inget val har sparats än. */
export function trackingOptOutUnset(): boolean {
  if (typeof window === "undefined") return false;
  try {
    return window.localStorage.getItem(OPTOUT_KEY) === null;
  } catch {
    return false;
  }
}


export type RangePreset =
  | "live"
  | "today"
  | "yesterday"
  | "24h"
  | "3d"
  | "5d"
  | "7d"
  | "10d"
  | "14d"
  | "30d"
  | "month"
  | "90d"
  | "half"
  | "year"
  | "all"
  | "custom";

export const RANGE_PRESETS: { value: RangePreset; label: string; hours: number | null }[] = [
  { value: "live", label: "Just nu (30 min)", hours: 0.5 },
  { value: "today", label: "Idag", hours: null },
  { value: "yesterday", label: "Igår", hours: null },
  { value: "24h", label: "24 timmar", hours: 24 },
  { value: "3d", label: "3 dagar", hours: 72 },
  { value: "5d", label: "5 dagar", hours: 120 },
  { value: "7d", label: "7 dagar", hours: 168 },
  { value: "10d", label: "10 dagar", hours: 240 },
  { value: "14d", label: "14 dagar", hours: 336 },
  { value: "30d", label: "30 dagar", hours: 720 },
  { value: "month", label: "Denna månad", hours: null },
  { value: "90d", label: "90 dagar", hours: 2160 },
  { value: "half", label: "Halvår", hours: 4320 },
  { value: "year", label: "I år", hours: null },
  { value: "all", label: "Allt", hours: null },
  { value: "custom", label: "Eget spann", hours: null },
];

export type Bucket = "hour" | "day" | "week";

export type AnalyticsFilters = {
  preset: RangePreset;
  from?: string | null;
  to?: string | null;
  bucket: Bucket | "auto";
  compare: boolean;
  domain: DomainScope;
  hostKind: HostKind | "all";
  country: string | "all";
  device: string | "all";
  authed: "all" | "yes" | "no";
  appMode: "all" | "pwa" | "browser";
  excludeAdmins: boolean;
  /** Nedborrning: klick på land/region/stad/källa filtrerar hela sidan. */
  region: string | "all";
  city: string | "all";
  referrer: string | "all";
  refresh: 0 | 30 | 60;
  tab: string;
};

export const DEFAULT_FILTERS: AnalyticsFilters = {
  preset: "7d",
  from: null,
  to: null,
  bucket: "auto",
  compare: true,
  domain: "own",
  hostKind: "all",
  country: "all",
  device: "all",
  authed: "all",
  appMode: "all",
  excludeAdmins: true,
  region: "all",
  city: "all",
  referrer: "all",
  refresh: 0,
  tab: "overview",
};

/** En rad i detaljvyn bakom ett nyckeltal. */
export type EventDetailRow = {
  createdAt: string;
  kind: string;
  path: string;
  title: string;
  label: string;
  adSlug: string | null;
  adTitle: string | null;
  adCategory: string | null;
  country: string;
  region: string;
  city: string;
  device: string;
  browser: string;
  os: string;
  referrerHost: string;
  referrerKind: string;
  hostKind: string;
  isAuthenticated: boolean;
  plan: string;
  isNewVisitor: boolean;
  visitorId: string;
};

export type EventDetails = {
  title: string;
  total: number;
  rows: EventDetailRow[];
  breakdowns: { label: string; items: NameCount[] }[];
};

/** Vilka nyckeltal som går att borra i, och hur de plockas fram. */
export type DetailKey =
  | "visitors"
  | "sessions"
  | "pageviews"
  | "adViews"
  | "contacts"
  | "publishes"
  | "searches"
  | "shares"
  | "newVisitors"
  | "returningVisitors"
  | "logins"
  | "loggedInVisitors"
  | "consent"
  | "path"
  | "adSlug"
  | "referrer"
  | "country"
  | "region"
  | "city"
  | "device"
  | "click";




/** Räknar ut faktiskt tidsspann (ISO-strängar) utifrån filtren. */
export function resolveRange(f: AnalyticsFilters): { from: string; to: string } {
  const now = new Date();
  let to = f.preset === "custom" && f.to ? new Date(`${f.to.slice(0, 10)}T23:59:59`) : now;
  let from: Date;
  const midnight = () => {
    const d = new Date(now);
    d.setHours(0, 0, 0, 0);
    return d;
  };

  if (f.preset === "custom" && f.from) {
    from = new Date(f.from);
  } else if (f.preset === "today") {
    from = midnight();
  } else if (f.preset === "yesterday") {
    to = midnight();
    from = new Date(to.getTime() - 24 * 3_600_000);
  } else if (f.preset === "month") {
    from = midnight();
    from.setDate(1);
  } else if (f.preset === "year") {
    from = new Date(now.getFullYear(), 0, 1);
  } else if (f.preset === "all") {
    from = new Date(2020, 0, 1);
  } else {
    const hours = RANGE_PRESETS.find((p) => p.value === f.preset)?.hours ?? 168;
    from = new Date(now.getTime() - hours * 3_600_000);
  }

  return { from: from.toISOString(), to: to.toISOString() };
}


/** Väljer upplösning automatiskt när användaren inte valt själv. */
export function resolveBucket(f: AnalyticsFilters): Bucket {
  if (f.bucket !== "auto") return f.bucket;
  const { from, to } = resolveRange(f);
  const hours = (new Date(to).getTime() - new Date(from).getTime()) / 3_600_000;
  if (hours <= 72) return "hour";
  if (hours <= 24 * 70) return "day";
  return "week";
}

export const BUCKET_LABEL: Record<Bucket, string> = {
  hour: "Per timme",
  day: "Per dag",
  week: "Per vecka",
};

export type SeriesPoint = {
  t: string;
  visitors: number;
  sessions: number;
  pageviews: number;
  adViews: number;
  contacts: number;
  publishes: number;
};

export type NameCount = { name: string; value: number; extra?: number };

export type AdRow = {
  slug: string;
  title: string;
  category: string;
  price: number;
  imageUrl: string;
  location: string;
  status: string;
  views: number;
  unique: number;
  contacts: number;
  shares: number;
};

export type CohortRow = { cohort: string; size: number; retention: (number | null)[] };

export type AnalyticsReport = {
  totals: {
    visitors: number;
    sessions: number;
    pageviews: number;
    adViews: number;
    contacts: number;
    publishes: number;
    searches: number;
    shares: number;
    newVisitors: number;
    returningVisitors: number;
    pagesPerSession: number;
    contactRate: number;
    logins: number;
    uniqueLogins: number;
    repeatLogins: number;
    loggedInVisitors: number;
  };

  previous: AnalyticsReport["totals"] | null;
  series: SeriesPoint[];
  pages: NameCount[];
  ads: AdRow[];
  referrers: NameCount[];
  hosts: NameCount[];
  countries: NameCount[];
  regions: NameCount[];
  cities: NameCount[];
  devices: NameCount[];
  browsers: NameCount[];
  systems: NameCount[];
  languages: NameCount[];
  plans: NameCount[];
  landing: NameCount[];
  exits: NameCount[];
  clicks: NameCount[];
  scroll: NameCount[];
  heatmap: { dow: number; hour: number; count: number }[];
  pwa: {
    visitors: number;
    sessions: number;
    pageviews: number;
    visitorShare: number;
    sessionShare: number;
    series: { t: string; app: number; browser: number }[];
    cities: NameCount[];
    countries: NameCount[];
    regions: NameCount[];
    systems: NameCount[];
    devices: NameCount[];
    pages: NameCount[];
  };
  cookies: {
    all: number;
    necessary: number;
    custom: number;
    acceptRate: number;
    series: { t: string; all: number; necessary: number; custom: number }[];
    byHost: NameCount[];
    byCountry: NameCount[];
    shown: number;
    settingsOpened: number;
    policyOpened: number;
    changed: number;
    answered: number;
    answerRate: number;
    analyticsYes: number;
  };

  funnels: {
    label: string;
    steps: { label: string; value: number; hint: string }[];
  }[];
  cohorts: CohortRow[];
  live: {
    last5: number;
    last30: number;
    pages: NameCount[];
    countries: NameCount[];
  };
  meta: { from: string; to: string; bucket: Bucket; sampled: boolean; rows: number };
};

/** Kort, mänsklig förklaring till varje nyckeltal. */
export const METRIC_HELP: Record<string, string> = {
  visitors:
    "Unika besökare: varje enhet räknas EN gång i perioden, hur många gånger den än kommer tillbaka. 1 person som besöker sajten tre gånger = 1 besökare.",
  sessions:
    "Besök (sessioner): varje besökstillfälle räknas för sig. Samma person som kommer tillbaka tre gånger = 3 besök. Sessionen tar slut när fliken stängs.",
  pageviews: "Sidvisningar = varje gång en sida laddas eller man byter sida i appen.",
  adViews: "Annonsvisningar = antal gånger en annonssida öppnats.",
  contacts: "Kontakter = antal gånger någon skickat ett meddelande till en säljare.",
  publishes: "Publiceringar = antal annonser som lagts ut färdigt under perioden.",

  searches: "Sökningar = vanliga sökningar och AI-sökningar på sajten.",
  shares: "Delningar = antal gånger någon delat en annons.",
  newVisitors: "Nya besökare = besökare som syns för första gången i den här statistiken.",
  returningVisitors: "Återkommande = besökare som varit här tidigare.",
  pagesPerSession: "Sidor per session = hur många sidor en typisk besökare tittar på per besök.",
  contactRate: "Kontaktgrad = andel annonsvisningar som leder till ett meddelande till säljaren.",
  uniqueLogins:
    "Unika inloggningar = antal olika konton som loggat in under perioden. Loggar samma person in tio gånger räknas det som 1.",
  logins:
    "Inloggningar totalt = alla inloggningstillfällen. Samma konto räknas igen tidigast efter 30 minuter, så tokenförnyelser inte dubbelräknas.",
  repeatLogins:
    "Konton som loggat in flera gånger under perioden (mer än en inloggning).",
  loggedInVisitors: "Besökare som var inloggade någon gång under sitt besök.",

  acceptRate: "Andel som godkänner alla cookies av alla som svarat på bannern.",
  answerRate:
    "Svarsfrekvens = hur många som faktiskt tryckte på något i cookie-rutan av alla som fick se den.",
  analyticsYes: "Antal svar där besökaren tillät analyscookies (godkände allt eller eget val).",
  landing: "Landningssidor = första sidan i besöket. Visar var trafiken faktiskt kommer in.",
  exits: "Utgångssidor = sista sidan i besöket. Många utgångar kan betyda en återvändsgränd.",
  clicks: "Klick på viktiga knappar, t.ex. Börja här, publicera, kontakta säljare och dela.",
  scroll: "Skrolldjup = hur långt ner på sidan besökarna i snitt läser, i procent.",
  heatmap:
    "Rutnätet visar vilken veckodag och timme besöken sker. Starkare färg = fler sidvisningar.",
  domain:
    "Domänvalet styr allt på sidan. sellavo.com är riktig trafik, Lovable är vår egen testtrafik.",
  excludeAdmins:
    "Räknar bort besök från inloggade admin-konton, så våra egna klick inte färgar siffrorna.",
  excludeSelf:
    "Stänger av spårning i just den här webbläsaren. Då hamnar dina egna besök aldrig i statistiken.",
};


export function pctChange(now: number, before: number | undefined | null): number | null {
  if (before === undefined || before === null) return null;
  if (before === 0) return now === 0 ? 0 : 100;
  return ((now - before) / before) * 100;
}




const LANG_NAMES: Record<string, string> = {
  sv: "Svenska",
  en: "Engelska",
  no: "Norska",
  nb: "Norska",
  da: "Danska",
  fi: "Finska",
  de: "Tyska",
  fr: "Franska",
  es: "Spanska",
  it: "Italienska",
  pl: "Polska",
  nl: "Nederländska",
  pt: "Portugisiska",
  ar: "Arabiska",
  fa: "Persiska",
  so: "Somaliska",
  tr: "Turkiska",
  uk: "Ukrainska",
  ru: "Ryska",
  zh: "Kinesiska",
};

/** "sv-SE" → "Svenska (Sverige)". */
export function langLabel(tag: string | null | undefined): string {
  const raw = (tag ?? "").trim();
  if (!raw) return "(okänt språk)";
  const [base, region] = raw.split("-");
  const name = LANG_NAMES[(base ?? "").toLowerCase()] ?? raw;
  if (!region) return name;
  // Landsnamnet slås upp med webbläsarens egna namnlista, så den stora
  // landslistan inte behöver ingå i sajtens startpaket.
  let country = "";
  try {
    country = new Intl.DisplayNames(["sv"], { type: "region" }).of(region.toUpperCase()) ?? "";
  } catch {
    country = region.toUpperCase();
  }
  return country ? `${name} (${country})` : name;
}

const PLAN_LABELS: Record<string, string> = {
  anon: "Utloggad",
  free: "Gratis",
  plus: "Plus",
  pro: "Pro",
};

/** "free" → "Gratis". */
export function planLabel(plan: string | null | undefined): string {
  const p = (plan ?? "").trim().toLowerCase();
  if (!p) return "(okänd kontotyp)";
  return PLAN_LABELS[p] ?? p;
}
